import { useCallback, useState, useRef, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';
import cardValidator from 'card-validator';
import { CardData, useAcceptJs } from 'react-acceptjs';
import _ from 'lodash';

import { PaymentForm } from '@/shared/components/payment-form';
import { IPaymentFormData } from '@/shared/components/payment-form/constants';
import { selectPaymentInfoPopupShown } from '@/store/slices/auth/selectors';
import { markPaymentInfoPopupShown } from '@/store/slices/auth';
import { useAppDispatch, useAppSelector } from '@/shared/hooks/store';
import { selectOnboardingUser, selectCreditCards } from '@/store/slices/onboarding/selectors';
import { AuthCardCreateRequest } from '@/shared/endpoints/customers';
import { CustomersController } from '@/shared/endpoints/customers';
import { setPageType } from '@/shared/helpers/page-type';
import { useHeaderHeight } from '@/shared/components/header-height-context';
import { Alert, Button, RadioGroup } from '@/shared/ui';

import CardList from './components/cardList';
import CopaysQuestions from './components/copays-questions';
import AddPaymentLaterConfirm from './components/add-payment-later-confirm';
import AddCardSuccess from './components/add-card-success';
import { paymentMethodNote, paymentMethodSuccessNote } from './constants';

import CreditCardIcon from '@/assets/icons/credit-card.png';

import './style.css';

export interface Response {
    opaqueData: {
        dataDescriptor: string;
        dataValue: string;
    };
    messages: {
        resultCode: string;
        message: {
            code: string;
            text: string;
        }[];
    };
}

export interface IProps {
    paymentOption: PaymentOption | null,
    paymentMethodError?: boolean;
    setPaymentOption(value: PaymentOption): void;
    formikControl?: ReturnType<typeof useFormik<IPaymentFormData>>;
};

type PaymentOption = 'now' | 'later';

const ReviewPayment = ({
    formikControl,
    paymentOption,
    paymentMethodError,
    setPaymentOption,
}: IProps) => {
    const formFocusedRef = useRef(false);
    const dispatch = useAppDispatch();
    const headerHeight = useHeaderHeight();
    const currentUser = useAppSelector(selectOnboardingUser)!;
    const paymentInfoPopupShown = !!useAppSelector(selectPaymentInfoPopupShown);
    const existingCreditCards = useAppSelector(selectCreditCards);
    const [addPaymentLoading, setAddPaymentLoading] = useState(false);
    const [isCopaysQuestionsOpen, setIsCopaysQuestionsOpen] = useState(false);
    const [addLaterConfirmOpen, setAddLaterConfirmOpen] = useState(false);
    const [cardSuccessOpen, setCardSuccessOpen] = useState(false);
    const { dispatchData } = useAcceptJs({
        authData: {
            apiLoginID: import.meta.env.VITE_APP_AUTHORIZE_NET_LOGIN_ID,
            clientKey: import.meta.env.VITE_APP_AUTHORIZE_NET_CLIENT_KEY
        },
        environment: import.meta.env.VITE_APP_IS_PRODUCTION === 'true' ? 'PRODUCTION' : 'SANDBOX'
    });

    const _conv_page_type = existingCreditCards.length ? 'payments_credit_cards' : 'payment_form';
    useEffect(() => {
        setPageType(_conv_page_type);
    }, [_conv_page_type]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (paymentInfoPopupShown || existingCreditCards.length) return;
            if (!paymentInfoPopupShown && !formFocusedRef.current) {
                dispatch(markPaymentInfoPopupShown());
            }
        }, 5000);

        return () => clearTimeout(timer);
    }, [dispatch, paymentInfoPopupShown, existingCreditCards.length]);

    const handleSave = useCallback(
        async (formData: IPaymentFormData) => {
            try {
                const { addressLine1, addressLine2, unit, city, state, zip } = currentUser.addresses[0];
                if (formData.hasDifferentBillingAddress === 1) {
                    formData.billingDetails = {
                        ...formData.billingDetails,
                        addressLine1: addressLine1!,
                        addressLine2: addressLine2!,
                        unit,
                        city,
                        state,
                        zip
                    };
                }
                setAddPaymentLoading(true);
                const [month, year] = formData.expirationDate.split('/');
                const fullName = `${formData?.firstName} ${formData?.lastName}`;
                const cardData: CardData = {
                    cardNumber: formData.cardNumber.toString().replaceAll(' ', ''),
                    month,
                    year,
                    cardCode: formData.cvv.toString(),
                    fullName,
                    zip: formData?.billingDetails?.zip || ''
                };
                const response: Response = await dispatchData({ cardData });
                const brand = cardValidator.number(formData.cardNumber)?.card?.type;
                const payload: AuthCardCreateRequest = {
                    cardId: response.opaqueData?.dataValue,
                    name: fullName,
                    lastFourDigit: formData.cardNumber.slice(-4),
                    expiryMonth: formData.expirationDate.split('/')[0],
                    expiryYear: formData.expirationDate.split('/')[1],
                    brand,
                    preferred: true,
                    accountId: currentUser?.id,
                    billingDetails: {
                        firstName: formData.firstName,
                        lastName: formData.lastName,
                        ...formData.billingDetails
                    }
                };

                const result = await dispatch(CustomersController.addPaymentMethod(payload));

                if (!result?.type?.includes('fulfilled')) {
                    throw new Error('Error adding payment method');
                }

                setCardSuccessOpen(true);
            } catch (error) {
                toast('Failed to save payment method', { autoClose: 3000, type: 'error' });
            } finally {
                setAddPaymentLoading(false);
            }
        },
        [currentUser?.id, dispatch, dispatchData, currentUser.addresses]
    );

    const changePaymentOption = useCallback((value: string | number | boolean) => {
        setPaymentOption(value as PaymentOption);
        if (value === 'later') setAddLaterConfirmOpen(true);
    }, []);

    return (
        <div className="flex flex-col pt-5 md:pt-9">
            <h3 className="font-atkinsonBold text-xl mb-1">Payment Details</h3>
            <p className="text-base font-atkinsonHyperlegible mb-[0.625rem]">
                Save your card details for future automatic copay payments and OTC purchases.
                <br />
                <b className="font-atkinsonBold">Note, your card will not be charged with your submission.</b>
                &nbsp;Monthly payments will be begin once your first MedBox ships.
            </p>

            <Button className="mb-4 mr-auto" variant="text" onClick={() => setIsCopaysQuestionsOpen(true)}>
                Questions About Copays?
            </Button>

            {existingCreditCards.length ? (
                <CardList className="mb-5" list={existingCreditCards} />
            ) : (
                <RadioGroup
                    formControl={false}
                    value={paymentOption}
                    onSelectChange={changePaymentOption}
                    items={[
                        {
                            label: <span className="font-atkinsonBold flex items-center">
                                <img
                                    src={CreditCardIcon}
                                    alt="credit card"
                                    className="w-[1.125rem] mr-2"
                                    width={18}
                                />
                                Add Pament Method
                            </span>,
                            value: 'now',
                            children: paymentOption === 'now' ? (
                                <PaymentForm
                                    hideIconsFromInput
                                    awarenessText=""
                                    className="payment-form-onboarding"
                                    isPreferred
                                    showCardIcons
                                    userAddress={currentUser?.addresses[0].fullAddress || ''}
                                    addPaymentLoading={addPaymentLoading}
                                    onSave={handleSave}
                                    headerOffset={headerHeight}
                                    withinModal={false}
                                    formikControl={formikControl}
                                />
                            ) : null,
                        },
                        {
                            label: 'Add Payment Later',
                            value: 'later'
                        },
                    ]}
                    name="payment-options"
                    radioGroupClassName={`payment-options-radio-group ${paymentMethodError ? 'payment-options-radio-group-error' : ''}`}
                    buttonClassName="payment-options-radio"
                    dataTestId="payment-options"
                    optionType="radio"
                />
            )}

            {paymentMethodError ? (
                <Alert
                    label={
                        <span className="font-normal font-atkinsonHyperlegible">
                            <span className="font-atkinsonBold">Please add payment method or select to add later.</span>
                            <br />
                            {paymentMethodNote}
                        </span>
                    }
                    variant="error"
                />
            ) : (
                <Alert
                    label={
                        existingCreditCards.length ? (
                            <span className="font-normal font-atkinsonHyperlegible">
                                <span className="font-atkinsonBold">Note, your card will not be charged until shipment.</span>
                                <br />
                                {paymentMethodSuccessNote}
                            </span>
                        ) : (
                            <span className="font-normal font-atkinsonHyperlegible">
                                {paymentMethodNote}
                                <br />
                                <span className="font-atkinsonBold">Note, your card will not be charged until shipment.</span>
                            </span>
                        )
                    }
                    variant={existingCreditCards.length ? 'success' : 'warning'}
                />
            )}

            <CopaysQuestions
                open={isCopaysQuestionsOpen}
                onClose={() => setIsCopaysQuestionsOpen(false)}
            />

            <AddCardSuccess
                open={cardSuccessOpen}
                onContinue={() => setCardSuccessOpen(false)}
            />

            <AddPaymentLaterConfirm
                open={addLaterConfirmOpen}
                onConfirm={() => setAddLaterConfirmOpen(false)}
                onCancel={() => { setAddLaterConfirmOpen(false); setPaymentOption('now'); }}
            />
        </div>
    );
};

export default ReviewPayment;

// export default withScrollRestoration(Payment);
